import { useState, useEffect } from 'react';
import { googleOAuthService } from '../services/googleOAuthService';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

export const useGoogleOAuth = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasCheckedBackend, setHasCheckedBackend] = useState(false);

  // Check connection status on mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Check OAuth callback on mount
  useEffect(() => {
    handleOAuthCallback();
  }, []);

  const checkConnectionStatus = async () => {
    // Only check backend once per session
    if (hasCheckedBackend) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Check with backend first
      try {
        const backendResponse = await authService.checkGoogleConnection();
        
        // Check if the response indicates connected status
        const isBackendConnected = backendResponse.message === "Google account is connected." && backendResponse.data;
        
        setIsConnected(isBackendConnected);
        setHasCheckedBackend(true);
        
        if (isBackendConnected) {
          // Don't show user profile details - only connection status
          setUserProfile(null);
        }
        
        return;
      } catch (error) {
        console.log('Backend connection check failed, account not connected:', error.message);
        setIsConnected(false);
        setUserProfile(null);
        setHasCheckedBackend(true);
        return;
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
      setIsConnected(false);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthCallback = async () => {
    // Check if current URL has OAuth callback code
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      console.error('OAuth error:', error);
      toast.error('Google authentication failed');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && state === 'admin_oauth') {
      setIsConnecting(true);
      try {
        // Exchange code for tokens
        const tokenData = await googleOAuthService.handleCallback(code);
        
        // Get user profile
        const profile = await googleOAuthService.getUserProfile();
        setUserProfile(profile);
        
        // Prepare Google OAuth data for backend
        const googleOAuthData = {
          google_id: profile.id,
          // email: profile.email,
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token
          // expiry_time: expiryTime.toISOString()
        };
        
        // Send token to backend
        await authService.sendGoogleToken(googleOAuthData);
        
        setIsConnected(true);
        toast.success(`Connected to Google as ${profile.name}`);
        
        // Reset backend check flag to allow fresh status check
        setHasCheckedBackend(false);
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('OAuth callback handling failed:', error);
        toast.error('Failed to connect Google account');
        disconnect();
      } finally {
        setIsConnecting(false);
      }
    }
  };

  const connectToGoogle = () => {
    if (!googleOAuthService.clientId) {
      toast.error('Google OAuth not configured');
      return;
    }

    setIsConnecting(true);
    const authUrl = googleOAuthService.generateAuthUrl();
    
    // Redirect to Google OAuth
    window.location.href = authUrl;
  };

  const disconnect = async () => {
    try {
      setIsConnecting(true);
      
      // Disconnect from backend first
      await authService.disconnectGoogle();
      
      // Clear local tokens
      googleOAuthService.disconnect();
      
      setIsConnected(false);
      setUserProfile(null);
      setHasCheckedBackend(false); // Reset backend check flag
      toast.success('Disconnected from Google');
    } catch (error) {
      console.error('Disconnect failed:', error);
      toast.error('Failed to disconnect from Google');
      
      // Force local disconnect even if backend fails
      googleOAuthService.disconnect();
      setIsConnected(false);
      setUserProfile(null);
      setHasCheckedBackend(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const getAccessToken = () => {
    return googleOAuthService.getAccessToken();
  };

  return {
    isConnected,
    isConnecting,
    userProfile,
    loading,
    connectToGoogle,
    disconnect, // Now properly disconnects from both backend and local storage
    getAccessToken,
    checkConnectionStatus
  };
}; 